import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV, COMPANY } from "@/data/content";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-slate-950/75 backdrop-blur-2xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <span className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-violet-500 shadow-[0_0_24px_rgba(0,229,255,0.35)]">
            <span className="absolute inset-[2px] rounded-[10px] bg-slate-950 flex items-center justify-center font-display text-[13px] font-black text-cyan-300">
              DP
            </span>
          </span>
          <div className="leading-tight">
            <div className="font-display font-bold text-[15px] tracking-tight text-white">
              DevOps<span className="text-cyan-400">Pro</span>Cloud
            </div>
            <div className="font-mono-plex text-[9px] tracking-[0.28em] text-slate-400 uppercase">
              Cloud · Secure · Scale
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-[13px] font-medium tracking-tight transition-all ${
                  isActive
                    ? "text-cyan-300 bg-cyan-500/10"
                    : "text-slate-300 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={COMPANY.phoneHref}
            data-testid="nav-phone"
            className="font-mono-plex text-[12px] text-slate-400 hover:text-cyan-300 transition-colors"
          >
            {COMPANY.phone}
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta-book"
            className="group inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-cyan-400 text-slate-950 font-semibold text-[13px] hover:bg-cyan-300 transition-all shadow-[0_0_24px_rgba(0,229,255,0.35)]"
          >
            Book a call
            <ArrowUpRight size={15} className="group-hover:rotate-45 transition-transform" />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 rounded-lg border border-white/10 text-white"
          aria-label="Menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-slate-950/95 backdrop-blur-2xl border-b border-white/10">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-sm font-medium ${
                    isActive ? "text-cyan-300 bg-cyan-500/10" : "text-slate-200 hover:bg-white/5"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-2 text-center px-5 py-3 rounded-full bg-cyan-400 text-slate-950 font-semibold text-sm"
            >
              Book a call
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
